import type { Building } from "building-types/building/buildingTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import type {
  StairBlock,
  StairBlockDatumHosting,
  StairBlockEdgeHosting,
  StairBlockHosting,
  StairBlockLanding,
  StairBlockResolved,
  StairBlockRun,
  StairBlockRunResolved,
  StairHosts,
  StairSystem,
} from "building-types/stairs/stairTypes";
import { StairVerticalAlignment, StairBlockType } from "building-types/stairs/stairTypesEnum";
import { StairErrors } from "building-types/stairs/stairTypesEnum";
import type { RWTable } from "common/data/table";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { worldInchesNew } from "common/utils/nominalNumbers";
import { defaultStairConfig } from "doc/elements/stairSystem/stairConstants";
import { hostToChildDeltas, isAligned } from "doc/hosting";
import { line2Normal } from "geom/line2";
import { mat3Translate } from "geom/mat3";
import { polyLine2Copy, polyLine2Edge, polyLine2Rotate, polyLine2SetCCW, polyLine2TransformCopy } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { vec2Scale } from "geom/vec2";
import { getDatumZ } from "../datums";
import { isValidBlockRectangle } from "./stairBlockValidate";
import { getStairRunLengthAndNormal } from "./stairRun";
import { isEdgeHosted, isEdgeHosting, LastRiser, resolveRisers, resolveTreads } from "./stairUtil";

export function getInitialStairBlockTreadsAndRunHeights(
  stairBlocks: StairBlock[],
  desiredTreadDepth: number,
  minTreadDepth: number,
  maxTreadDepth: number,
): StairBlockResolved[] {
  const stairBlocksResolved: StairBlockResolved[] = [];
  for (const blockIndex of Object.keys(stairBlocks)) {
    const block = stairBlocks[blockIndex];

    if (!block.outline) {
      continue;
    }
    const { outline, edgeIds } = block;

    switch (block.type) {
      case StairBlockType.RUN: {
        const startEdgeIndex = edgeIds.indexOf(block.startEdgeId);
        if (startEdgeIndex === undefined) {
          continue;
        }
        const { length, normal } = getStairRunLengthAndNormal(outline, startEdgeIndex);
        const { treadCount, treadDepthActual } = resolveTreads(length, desiredTreadDepth, minTreadDepth, maxTreadDepth);
        const riserCount = treadCount + 1;

        assertIsNotNullOrUndefined(normal);

        const blockResolved: StairBlockRunResolved = {
          ...block,
          normal,
          length,
          riserCount,
          treadCount,
          treadDepthActual,
          outline: polyLine2Copy(outline),
          edgeIds,
          startEdgeIndex,
        };

        stairBlocksResolved.push(blockResolved);
        break;
      }
      case StairBlockType.LANDING: {
        stairBlocksResolved.push({ ...block, outline });
        break;
      }
    }
  }
  return stairBlocksResolved;
}

export function projectEdgeToRect([x1, y1, x2, y2]: PolyLine2, depth: number, reverse: boolean = false): PolyLine2 {
  let [nx, ny] = line2Normal(x1, y1, x2, y2);

  if (reverse) {
    nx *= -1;
    ny *= -1;
  }

  const [dx, dy] = vec2Scale(nx, ny, depth);

  const a = [x1, y1];
  const b = [x2, y2];
  const c = [x2 + dx, y2 + dy];
  const d = [x1 + dx, y1 + dy];

  return polyLine2New([...a, ...b, ...c, ...d, x1, y1]);
}

export interface StairSolverContext {
  readonly building: Pick<
    Building,
    "rooms" | "resolvedDatums" | "levels" | "roomFloorAssemblies" | "mirror" | "optionChoices"
  >;
  readonly stairSystem: StairSystem;
  readonly issues: BuildingGenerationIssue[];
  readonly stairBlocks: StairBlock[];
  /**
   * hosts will mutate during stair solving
   */
  hosts: StairHosts;
}

/**
 * graph of dependencies using only ids as nodes
 */
type Graph = {
  [id: string]: {
    /**
     * keep track only of outgoing edges
     */
    to: string[];
  };
};
type NMap = RWTable<boolean>;

export function makeStairBlockGraph(stairBlocks: StairBlock[]): Graph {
  // build graph of stair block dependencies
  const depGraph: Graph = {};
  for (const block of stairBlocks) {
    const to: string[] = [];
    depGraph[block.id] = { to };
    switch (block.type) {
      case StairBlockType.LANDING:
        if (block.hosting?.type === "EDGE") {
          if (block.hosting.hostId) {
            to.push(block.hosting.hostId);
          }
        }
        break;
      case StairBlockType.RUN:
        if (block.hostingLower?.type === "EDGE") {
          if (block.hostingLower.hostId) {
            to.push(block.hostingLower.hostId);
          }
        }
        if (block.hostingUpper?.type === "EDGE") {
          if (block.hostingUpper.hostId) {
            to.push(block.hostingUpper.hostId);
          }
        }
        break;
    }
  }
  return depGraph;
}

export function topologicalSort(g: Graph): string[] {
  const nodes = Object.keys(g);
  const visited: NMap = {};
  const stack: string[] = [];
  for (const node of nodes) {
    visited[node] = false;
  }
  for (const node of nodes) {
    if (!visited[node]) {
      topologicalSortUtil(g, node, visited, stack);
    }
  }
  return stack;
}

/**
 * @time O(V+E)
 */
function topologicalSortUtil(g: Graph, nodeId: string, visited: NMap, stack: string[]) {
  visited[nodeId] = true;
  for (const n of g[nodeId]?.to || []) {
    if (!visited[n]) {
      topologicalSortUtil(g, n, visited, stack);
    }
  }
  stack.unshift(nodeId);
}

export function subgraph(g: Graph, id: string, visited: NMap = {}, sub: Graph = {}): Graph {
  visited[id] = true;
  const node = g[id];
  if (!node) {
    return sub;
  }
  sub[id] = { to: [...node.to] };
  node.to.forEach((n) => {
    if (!visited[n]) {
      subgraph(g, n, visited, sub);
    }
  });
  return sub;
}

export function resolveStairSystemHosting(ctx: StairSolverContext): StairBlock[] {
  const { stairBlocks } = ctx;

  const graph = makeStairBlockGraph(ctx.stairBlocks);

  // TODO: To speed up the solver in the future, calculate the
  // subgraph of dependencies from the root block and solve
  // that instead of the whole graph.
  // this is currently not working because we topologically
  // sort and then reverse the sorted nodes below, so we also
  // need to add the ability to reverse a graph.
  // Currently the solver runs fast enough that it's not a
  // problem.
  // if (root) {
  // graph = subgraph(graph, root.id);
  // }

  const blockMap = stairBlocks.reduce(
    (acc, block) => {
      acc[block.id] = block;
      return acc;
    },
    {} as { [id: string]: StairBlock },
  );

  const resolved: StairBlock[] = [];

  // sort/solve stair blocks topologically so that we can refer
  // to host datums reliably
  const sorted = topologicalSort(graph).reverse();
  for (const blockId of sorted) {
    const block = blockMap[blockId];
    if (block) {
      resolved.push(resolveStairBlock(ctx, block));
    }
  }

  return resolved;
}

function resolveStairBlock(ctx: StairSolverContext, block: StairBlock): StairBlock {
  switch (block.type) {
    case StairBlockType.LANDING:
      block = resolveBlockHosting<StairBlockLanding>(ctx, block, block.hosting);

      if (isEdgeHosting(block.hosting)) {
        block = alignBlockToHost(ctx, block, block.hosting);
      }
      break;
    case StairBlockType.RUN:
      block = resolveBlockHosting(ctx, block, block.hostingUpper);
      block = resolveBlockHosting(ctx, block, block.hostingLower);

      block = resolveStairRunOutline(ctx, block);

      if (isEdgeHosting(block.hostingUpper)) {
        block = alignBlockToHost(ctx, block, block.hostingUpper);
      } else if (isEdgeHosting(block.hostingLower)) {
        block = alignBlockToHost(ctx, block, block.hostingLower);
      }

      break;
  }

  // add the possibly mutated block
  // back into the map of available
  // hosts
  ctx.hosts[block.id] = block;
  return block;
}

function getHostEdgeHeight({ building, hosts }: StairSolverContext, hosting?: StairBlockEdgeHosting): WorldInches {
  let height: number = 0;

  if (!building) {
    return worldInchesNew(height);
  }

  const host = hosting?.hostId ? hosts[hosting.hostId] : undefined;
  if (!host) {
    return worldInchesNew(height);
  }

  switch (host.entityType) {
    case "STAIR_BLOCK":
      switch (host.type) {
        case StairBlockType.LANDING:
          height = getDatumZ(host.datumVertical ? building.resolvedDatums[host.datumVertical] : undefined);
          break;
        case StairBlockType.RUN: {
          assertIsNotNullOrUndefined(hosting);
          switch (hosting.childAlignment) {
            case StairVerticalAlignment.TOP:
              height = getDatumZ(
                host.datumVerticalUpper ? building.resolvedDatums[host.datumVerticalUpper] : undefined,
              );
              break;
            case StairVerticalAlignment.BOTTOM:
              height = getDatumZ(
                host.datumVerticalLower ? building.resolvedDatums[host.datumVerticalLower] : undefined,
              );
              break;
          }
        }
      }
      break;
    case "ROOM":
      const datum = building.resolvedDatums[host.id];
      if (!datum) {
        height = worldInchesNew(0);
        break;
      }
      height = getDatumZ(datum);
      break;
  }

  return worldInchesNew(height + (hosting?.vertical?.offset || 0));
}

/**
 * resolving the stair run will adjust the outlines of the stair run
 * for rectangular blocks to fit between the hosts
 */
function resolveStairRunOutline(ctx: StairSolverContext, block: StairBlockRun): StairBlockRun {
  const { building } = ctx;

  if (!building) {
    return block;
  }

  // non-rectangular blocks will not have their outlines
  // adjusted, as there is no clear set of rules for how
  // this should happen
  // ... possibly a bounding box oriented to the edge-hosted
  // edge?
  if (!isValidBlockRectangle(block.outline)) {
    return block;
  }

  // non-edge-hosted blocks do not need to be further
  // adjusted
  if (!isEdgeHosted(block)) {
    return block;
  }

  // adjust run to fit between hosts
  let edgeHosting: StairBlockEdgeHosting | undefined = undefined;
  // let edgeHostLocation: StairBlockRunHostingEdgeType;
  if (block.hostingLower?.type === "EDGE") {
    edgeHosting = block.hostingLower;
    // edgeHostLocation = "LOWER";
  } else if (block.hostingUpper?.type === "EDGE") {
    edgeHosting = block.hostingUpper;
    // edgeHostLocation = "UPPER";
  }
  let datumHosting: StairBlockDatumHosting | undefined = undefined;
  if (block.hostingLower?.type === "DATUM") {
    datumHosting = block.hostingLower;
  } else if (block.hostingUpper?.type === "DATUM") {
    datumHosting = block.hostingUpper;
  }

  // TODO: ANDREW if there is no datum set, what should we do?
  if (!datumHosting?.datum) {
    return block;
  }

  const datumHostingDatum = building.resolvedDatums[datumHosting.datum];

  if (!datumHostingDatum) {
    return block;
  }

  // all outlines at this point are rectangular, so we can safely
  // assume they have 4 edges
  const bottomEdgeIndex = block.edgeIds.indexOf(block.startEdgeId);

  // this edge has been matched to the host edge
  // and does not move
  const springEdge = polyLine2Edge(block.outline, bottomEdgeIndex);
  const edgeHeight = getHostEdgeHeight(ctx, edgeHosting);

  const datumHeight = getDatumZ(datumHostingDatum);
  const height = Math.abs(edgeHeight - datumHeight + (datumHosting.offset || 0));

  const reverseProject = true;

  const {
    config: {
      desiredRiserHeight = defaultStairConfig.desiredRiserHeight,
      maxRiserHeight = defaultStairConfig.maxRiserHeight,
      desiredTreadDepth = defaultStairConfig.desiredTreadDepth,
    } = defaultStairConfig,
  } = ctx.stairSystem;

  const { riserCount } = resolveRisers(height, desiredRiserHeight, maxRiserHeight, LastRiser.NOT_ON_STAIR);
  const depth = Math.abs(riserCount * desiredTreadDepth);
  const projectedOutline = polyLine2SetCCW(projectEdgeToRect(springEdge, depth, reverseProject));

  block = {
    ...block,
    // we've rebuilt the outline, so the old startEdgeId is meaningless
    startEdgeId: block.edgeIds[0],
    outline: projectedOutline,
  };

  return block;
}

function resolveBlockHosting<T extends StairBlock>(
  ctx: StairSolverContext,
  block: T,
  hosting: StairBlockHosting | undefined,
): T {
  switch (hosting?.type) {
    case "EDGE":
      return resolveBlockEdgeHosting(ctx, block, hosting);
    case "DATUM":
      return resolveBlockDatumHosting(ctx, block, hosting);
    default:
      return block;
  }
}

function alignBlockToHost<T extends StairBlock>(
  { hosts, building, issues }: StairSolverContext,
  block: T,
  hosting: StairBlockEdgeHosting,
): T {
  if (!building) {
    return block;
  }

  const { mirror = false } = building;
  const { hostId, lateral, horizontal, childEdgeId } = hosting;
  const { hostEdgeId } = hosting;

  const host = hostId ? hosts[hostId] : undefined;
  if (!host) {
    issues.push({
      type: BuildingGenerationIssueType.STAIR_GENERATION_ISSUE,
      message: StairErrors.HOST_NOT_FOUND,
      handle: { type: "STAIR_BLOCK", id: block.id },
    });
    return block;
  }

  const childEdgeIdx = childEdgeId ? block.edgeIds.indexOf(childEdgeId) : -1; // -1 seems not desirable but keeps existing behavior

  let blockOutline = block.outline;

  const hostEdge = polyLine2Edge(host.outline, hostEdgeId ? host.edgeIds.indexOf(hostEdgeId) : -1); // -1 seems not desirable but keeps existing behavior
  const selfEdge = polyLine2Edge(blockOutline, childEdgeIdx);

  const deltas = hostToChildDeltas(mirror, hostEdge as any as number[], selfEdge as any as number[], {
    lateral,
    horizontal,
  });

  if (isAligned(deltas)) {
    return block;
  }

  const {
    theta,
    translation: [tx, ty],
    childMidPt: [cx, cy],
  } = deltas;
  const rotated = polyLine2Rotate(block.outline, theta, cx, cy);
  blockOutline = polyLine2TransformCopy(rotated, mat3Translate(tx, ty));

  return {
    ...block,
    outline: blockOutline,
  };
}

/**
 * resolves block location in XY space without changing the outline
 */
function resolveBlockEdgeHosting<T extends StairBlock>(
  { hosts }: StairSolverContext,
  block: T,
  hosting: StairBlockEdgeHosting,
): T {
  const { hostId } = hosting;
  let { hostEdgeId } = hosting;

  const host = hostId ? hosts[hostId] : undefined;
  if (!host) {
    return block;
  }

  // default to the first ID if not provided
  if (!hostEdgeId) {
    hostEdgeId = host.edgeIds[0];
  }

  if (!host) {
    console.warn("host not found", hostId);
    // clear out the stored datums
    switch (hosting.childType) {
      case StairBlockType.LANDING: {
        return {
          ...block,
          datumVertical: undefined,
        };
      }
      case StairBlockType.RUN: {
        switch (hosting.childAlignment) {
          case StairVerticalAlignment.BOTTOM: {
            return {
              ...block,
              datumVerticalLower: undefined,
            };
          }
          case StairVerticalAlignment.TOP: {
            return {
              ...block,
              datumVerticalUpper: undefined,
            };
          }
        }
      }
    }
  }
  switch (hosting.childType) {
    case StairBlockType.LANDING: {
      switch (host.entityType) {
        case "STAIR_BLOCK": {
          switch (host.type) {
            case StairBlockType.LANDING: {
              return {
                ...block,
                datumVertical: host.datumVertical,
                hosting: {
                  ...hosting,
                  hostEdgeId,
                },
              };
            }
            case StairBlockType.RUN: {
              // the "startEdge" of a RUN is the bottom edge
              // of the stair from which the stairs spring
              // upwards
              if (hosting.hostEdgeId === host.startEdgeId) {
                return {
                  ...block,
                  datumVertical: host.datumVerticalLower,
                  hosting: {
                    ...hosting,
                    hostEdgeId,
                  },
                };
              } else {
                return {
                  ...block,
                  datumVertical: host.datumVerticalUpper,
                  hosting: {
                    ...hosting,
                    hostEdgeId,
                  },
                };
              }
            }
          }
        }
        case "ROOM": {
          return {
            ...block,
            datumVertical: host.id,
            hosting: {
              ...hosting,
              hostEdgeId,
            },
          };
        }
      }
    }
    case StairBlockType.RUN: {
      switch (hosting.childAlignment) {
        case StairVerticalAlignment.BOTTOM: {
          return {
            ...block,
            datumVerticalLower: host.id,
            hostingLower: {
              ...hosting,
              hostEdgeId,
            },
          };
        }
        case StairVerticalAlignment.TOP: {
          return {
            ...block,
            datumVerticalUpper: host.id,
            hostingUpper: {
              ...hosting,
              hostEdgeId,
            },
          };
        }
      }
    }
  }
}

/**
 * resolves block datum references without resolving to specific Z values
 */
function resolveBlockDatumHosting<T extends StairBlock>(
  { hosts, building, issues }: StairSolverContext,
  block: T,
  hosting: StairBlockDatumHosting,
): T {
  if (!building) {
    return block;
  }

  const { resolvedDatums } = building;
  const { datum: datumId } = hosting;
  if (datumId && hosts[datumId]) {
    const host = hosts[datumId];
    switch (host.entityType) {
      case "ROOM": {
        const datum = resolvedDatums[datumId];
        if (datum) {
          switch (hosting.childType) {
            case StairBlockType.LANDING: {
              block = {
                ...block,
                datumVertical: datum.id,
              };
              break;
            }
            case StairBlockType.RUN: {
              switch (hosting.childAlignment) {
                case StairVerticalAlignment.BOTTOM: {
                  block = {
                    ...block,
                    datumVerticalLower: datum.id,
                  };
                  break;
                }
                case StairVerticalAlignment.TOP: {
                  block = {
                    ...block,
                    datumVerticalUpper: datum.id,
                  };
                  break;
                }
              }
              break;
            }
          }
        }
        break;
      }
      case "STAIR_BLOCK": {
        switch (host.type) {
          case StairBlockType.LANDING: {
            switch (hosting.childType) {
              case StairBlockType.LANDING: {
                block = {
                  ...block,
                  datumVertical: host.datumVertical,
                };
                break;
              }
              case StairBlockType.RUN: {
                switch (hosting.childAlignment) {
                  case StairVerticalAlignment.BOTTOM: {
                    block = {
                      ...block,
                      datumVerticalLower: host.datumVertical,
                    };
                    break;
                  }
                  case StairVerticalAlignment.TOP: {
                    block = {
                      ...block,
                      datumVerticalUpper: host.datumVertical,
                    };
                    break;
                  }
                }
              }
            }
            break;
          }
          case StairBlockType.RUN: {
            switch (hosting.childType) {
              case StairBlockType.LANDING: {
                block = {
                  ...block,
                  datumVertical: host.datumVerticalLower,
                };
                break;
              }
              case StairBlockType.RUN: {
                switch (hosting.childAlignment) {
                  case StairVerticalAlignment.BOTTOM: {
                    block = {
                      ...block,
                      datumVerticalLower: host.datumVerticalUpper,
                    };
                    break;
                  }
                  case StairVerticalAlignment.TOP: {
                    block = {
                      ...block,
                      datumVerticalUpper: host.datumVerticalLower,
                    };
                    break;
                  }
                }
              }
            }
            break;
          }
        }
        break;
      }
    }
  }
  const datum = datumId ? resolvedDatums[datumId] : undefined;
  if (!datum) {
    issues.push({
      type: BuildingGenerationIssueType.STAIR_GENERATION_ISSUE,
      message: StairErrors.DATUM_NOT_FOUND,
      handle: { type: "STAIR_BLOCK", id: block.id },
    });
    return block;
  }
  switch (block.type) {
    case StairBlockType.LANDING: {
      return {
        ...block,
        datumVertical: datum.id,
      };
    }
    case StairBlockType.RUN: {
      switch (hosting.childAlignment) {
        case StairVerticalAlignment.BOTTOM:
          return {
            ...block,
            datumVerticalLower: datum.id,
          };
        case StairVerticalAlignment.TOP:
          return {
            ...block,
            datumVerticalUpper: datum.id,
          };
      }
    }
  }
}

export function newHostingRunUpper(childEdgeIds: string[], childEdgeId: number = 0): StairBlockHosting {
  return {
    type: null as any,
    childAlignment: StairVerticalAlignment.TOP,
    childEdgeId: childEdgeIds[childEdgeId],
    childType: StairBlockType.RUN,
    offset: 0,
  };
}

export function newHostingRunLower(childEdgeIds: string[], childEdgeId: number = 0): StairBlockHosting {
  return {
    type: null as any,
    childAlignment: StairVerticalAlignment.BOTTOM,
    childEdgeId: childEdgeIds[childEdgeId],
    childType: StairBlockType.RUN,
    offset: 0,
  };
}
